import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../tokens/types';
import { PdfSection, PdfParagraph, PdfChip } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenParagraph, ScreenChip } from '../primitives/ScreenPrimitives';
import { View } from '@react-pdf/renderer';
import { fmtDate } from './formatUtils';

const PdfMca: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const m = data.mca;
    if (!m) return null;
    return (
        <PdfSection kicker="Filings" title="MCA documents summary" index={index} breakBefore={breakBefore}>
            <View style={{ flexDirection: 'row', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                <PdfChip tone={m.isFetching ? 'info' : 'LOW'}>{m.isFetching ? 'Fetch in progress' : 'Up to date'}</PdfChip>
                {m.updatedAt ? <PdfChip tone="muted">Updated {fmtDate(m.updatedAt)}</PdfChip> : null}
            </View>
            {m.summary ? (
                <PdfParagraph>{m.summary}</PdfParagraph>
            ) : m.isFetching ? (
                <PdfParagraph>
                    Summary is being generated from MCA filings. This can take up to 24 hours — refresh the report later.
                </PdfParagraph>
            ) : (
                <PdfParagraph>No summary available — refer to MCA portal for full filings.</PdfParagraph>
            )}
        </PdfSection>
    );
};

const ScreenMca: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const m = data.mca;
    if (!m) return null;
    return (
        <ScreenSection kicker="Filings" title="MCA documents summary" index={index}>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                <ScreenChip tone={m.isFetching ? 'info' : 'LOW'}>{m.isFetching ? 'Fetch in progress' : 'Up to date'}</ScreenChip>
                {m.updatedAt ? <ScreenChip tone="muted">Updated {fmtDate(m.updatedAt)}</ScreenChip> : null}
            </div>
            {m.summary ? (
                <ScreenParagraph>{m.summary}</ScreenParagraph>
            ) : m.isFetching ? (
                <ScreenParagraph>
                    Summary is being generated from MCA filings. This can take up to 24 hours — refresh the report later.
                </ScreenParagraph>
            ) : (
                <ScreenParagraph>No summary available — refer to MCA portal for full filings.</ScreenParagraph>
            )}
        </ScreenSection>
    );
};

export const mcaModule: ModuleDefinition = {
    meta: {
        id: 'mca',
        title: 'MCA documents summary',
        description: 'LLM summary of key MCA filings.',
        dataSource: 'MCA Docs API',
    },
    // Skip the module entirely while the LLM summary is still being generated —
    // a "Fetch in progress" placeholder page reads as broken in the export.
    // The module returns once an actual summary lands.
    hasData: (d) => !!d.mca && !d.mca.isFetching && !!d.mca.summary,
    Pdf: PdfMca,
    Screen: ScreenMca,
};
